import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { createPortal } from 'react-dom';
import {
    getVendors,
    addVendor,
    getNotePayableDetails,
    updateNotePayable,
    clearError,
    clearSuccess,
    getProjects,
    addProject,
    getCustomers,
    addCustomer,
    getTaxes,
    addTax,
    getCostCenters,
    addCostCenter
} from '../../../../redux/Slices/authSlice';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { FaPlus, FaTimes } from 'react-icons/fa';

const EditNotePayable = () => {
    // Handle form submit
    const handleSubmit = (e) => {
        e.preventDefault();
        // TODO: Add submit logic here
    };

    // Handle main form input change
    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm((prev) => ({ ...prev, [name]: value }));
    };

    // Handle vendor modal form change
    const handleVendorFormChange = (e) => {
        const { name, value } = e.target;
        setVendorForm((prev) => ({ ...prev, [name]: value }));
    };

    // Handle project modal form change
    const handleProjectFormChange = (e) => {
        const { name, value } = e.target;
        setProjectForm((prev) => ({ ...prev, [name]: value }));
    };
    // ...existing code...
    const { id } = useParams();
    const { t, i18n } = useTranslation('global');
    const isRTL = i18n.language === 'ar';
    const textAlign = isRTL ? 'right' : 'left';
    const dir = isRTL ? 'rtl' : 'ltr';
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const {
        notePayableDetails,
        vendors,
        vendorsPagination,
        projects,
        projectsPagination,
        customers,
        customersPagination,
        taxes,
        taxesPagination,
        costCenters,
        costCentersPagination,
        isLoading,
        error,
        success
    } = useSelector((state) => state.auth);

    // Ensure vendorsPagination is always an object to avoid undefined issues
    const vendorPaginationSafe = vendorsPagination || { current_page: 1, last_page: 1 };

    const [form, setForm] = useState({
        vendor_id: '',
        project_id: '',
        amount: '',
        issue_date: '',
        due_date: '',
        note_number: '',
        description: ''
    });

    const isSubOperationRef = useRef(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Dropdowns visibility
    const [vendorDropdownOpen, setVendorDropdownOpen] = useState(false);
    const [projectDropdownOpen, setProjectDropdownOpen] = useState(false);
    const [taxDropdownOpen, setTaxDropdownOpen] = useState(false);
    const [costCenterDropdownOpen, setCostCenterDropdownOpen] = useState(false);
    const [projModalCustomerDropdownOpen, setProjModalCustomerDropdownOpen] = useState(false);

    // Search terms
    const [vendorSearchTerm, setVendorSearchTerm] = useState('');
    const [projectSearchTerm, setProjectSearchTerm] = useState('');
    const [projModalCustomerSearchTerm, setProjModalCustomerSearchTerm] = useState('');
    const [taxSearchTerm, setTaxSearchTerm] = useState('');
    const [costCenterSearchTerm, setCostCenterSearchTerm] = useState('');

    // Pagination
    const [vendorCurrentPage, setVendorCurrentPage] = useState(1);
    const [projectCurrentPage, setProjectCurrentPage] = useState(1);
    const [projModalCustomerCurrentPage, setProjModalCustomerCurrentPage] = useState(1);
    const [taxCurrentPage, setTaxCurrentPage] = useState(1);
    const [costCenterCurrentPage, setCostCenterCurrentPage] = useState(1);

    // Refs
    const vendorSearchInputRef = useRef(null);
    const projectSearchInputRef = useRef(null);
    const projModalCustomerSearchInputRef = useRef(null);
    const taxSearchInputRef = useRef(null);
    const costCenterSearchInputRef = useRef(null);
    const vendorSearchTimeoutRef = useRef(null);
    const projectSearchTimeoutRef = useRef(null);
    const projModalCustomerSearchTimeoutRef = useRef(null);
    const projectNotesTextareaRef = useRef(null);
    const lastErrorRef = useRef({ message: '', time: 0 });

    // Modals
    const [showAddVendorModal, setShowAddVendorModal] = useState(false);
    const [showAddProjectModal, setShowAddProjectModal] = useState(false);
    const [showAddCustomerModal, setShowAddCustomerModal] = useState(false);
    const [showAddTaxModal, setShowAddTaxModal] = useState(false);
    const [showAddCostCenterModal, setShowAddCostCenterModal] = useState(false);

    // Sub-forms
    const [vendorForm, setVendorForm] = useState({ name_en: '', name_ar: '', contact_person: '', email: '', phone: '', address: '', tax_number: '', is_active: true });
    const [projectForm, setProjectForm] = useState({ name_en: '', name_ar: '', code: '', customer_id: '', start_date: '', end_date: '', status: 'planned', contract_value: '', tax_id: '', cost_center_id: '', notes: '' });
    const [customerForm, setCustomerForm] = useState({ name_ar: '', name_en: '', code: '', contact_person: '', email: '', phone: '', tax_number: '', address: '', notes: '', is_active: true });
    const [taxForm, setTaxForm] = useState({ name_ar: '', name_en: '', rate: '' });
    const [costCenterForm, setCostCenterForm] = useState({ name_ar: '', name_en: '', code: '' });

    const [isSubmittingVendor, setIsSubmittingVendor] = useState(false);
    const [isSubmittingProject, setIsSubmittingProject] = useState(false);
    const [isSubmittingCustomer, setIsSubmittingCustomer] = useState(false);

    // Initial load
    useEffect(() => {
        dispatch(clearError());
        dispatch(clearSuccess());
        dispatch(getNotePayableDetails(id));
        dispatch(getVendors({ page: 1, per_page: 10 }));
        dispatch(getProjects({ page: 1, per_page: 10 }));
        dispatch(getCustomers({ page: 1, per_page: 10 }));
        dispatch(getTaxes({ page: 1, per_page: 10 }));
        dispatch(getCostCenters({ page: 1, per_page: 10 }));
    }, [dispatch, id]);

    // Populate form
    useEffect(() => {
        if (notePayableDetails) {
            setForm({
                vendor_id: notePayableDetails.vendor_id || '',
                project_id: notePayableDetails.project_id || '',
                amount: notePayableDetails.amount || '',
                issue_date: notePayableDetails.issue_date || '',
                due_date: notePayableDetails.due_date || '',
                note_number: notePayableDetails.note_number || '',
                description: notePayableDetails.description || ''
            });
        }
    }, [notePayableDetails]);

    // Success handling
    useEffect(() => {
        if (success) {
            const messageKey = isSubOperationRef.current ? 'commonNew33333.success' : 'common.success';
            const message = typeof success === 'string' ? success : (success?.message || t(messageKey));

            toast.success(message, {
                rtl: isRTL,
                position: "top-right",
                autoClose: 3000
            });

            if (!isSubOperationRef.current) {
                const timer = setTimeout(() => {
                    navigate('/notes-payable');
                    dispatch(clearSuccess());
                }, 1500);
                return () => clearTimeout(timer);
            } else {
                dispatch(clearSuccess());
                isSubOperationRef.current = false;
            }
        }
    }, [success, dispatch, navigate, isRTL, t]);

    // Error handling
    useEffect(() => {
        if (error) {
            const now = Date.now();
            const last = lastErrorRef.current;
            const errorMessage = typeof error === 'string' ? error : (error?.message || t('common.error'));

            if (!last.message || last.message !== errorMessage || now - last.time > 2000) {
                toast.error(errorMessage, {
                    rtl: isRTL,
                    position: "top-right",
                    autoClose: 4000
                });
                lastErrorRef.current = { message: errorMessage, time: now };
            }

            const timer = setTimeout(() => {
                dispatch(clearError());
            }, 5000);
            return () => clearTimeout(timer);
        }
    }, [error, dispatch, isRTL, t]);

    // Search debouncing
    useEffect(() => {
        if (vendorSearchTimeoutRef.current) clearTimeout(vendorSearchTimeoutRef.current);
        vendorSearchTimeoutRef.current = setTimeout(() => {
            dispatch(getVendors({ page: vendorCurrentPage, per_page: 10, search: vendorSearchTerm || undefined }));
        }, 300);
        return () => clearTimeout(vendorSearchTimeoutRef.current);
    }, [vendorSearchTerm, vendorCurrentPage, dispatch]);

    useEffect(() => {
        if (projectSearchTimeoutRef.current) clearTimeout(projectSearchTimeoutRef.current);
        projectSearchTimeoutRef.current = setTimeout(() => {
            dispatch(getProjects({ page: projectCurrentPage, per_page: 10, search: projectSearchTerm || undefined }));
        }, 300);
        return () => clearTimeout(projectSearchTimeoutRef.current);
    }, [projectSearchTerm, projectCurrentPage, dispatch]);

    // Click outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (event.target.closest('[data-dropdown-container]')) return;
            setVendorDropdownOpen(false);
            setProjectDropdownOpen(false);
            setTaxDropdownOpen(false);
            setCostCenterDropdownOpen(false);
            setProjModalCustomerDropdownOpen(false);
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);


    // Sub-operation handlers
    const handleAddVendor = async (e) => {
        e.preventDefault();
        if (!vendorForm.name_en) {
            toast.error(t('vendors.add.nameEnRequired', { defaultValue: 'الاسم بالإنجليزية مطلوب' }), { rtl: isRTL });
            return;
        }
        if (!vendorForm.name_ar) {
            toast.error(t('vendors.add.nameArRequired', { defaultValue: 'الاسم بالعربية مطلوب' }), { rtl: isRTL });
            return;
        }
        try {
            setIsSubmittingVendor(true);
            isSubOperationRef.current = true;
            const result = await dispatch(addVendor({ ...vendorForm, is_active: vendorForm.is_active ? 1 : 0 })).unwrap();
            if (result) {
                setForm(prev => ({ ...prev, vendor_id: result.data.id }));
                // Refresh vendor list
                await dispatch(getVendors({ page: 1, per_page: 10 }));
                setShowAddVendorModal(false);
                setVendorForm({
                    name_en: '',
                    name_ar: '',
                    code: '',
                    contact_person: '',
                    email: '',
                    phone: '',
                    address: '',
                    tax_number: '',
                    is_active: true,
                    notes: ''
                });
            }
        } catch (error) {
            // Error handled by effect
        } finally {
            setIsSubmittingVendor(false);
        }
    };

    const handleAddProject = async (e) => {
        e.preventDefault();
        if (!projectForm.name_ar) {
            toast.error(t('quotations.add.modals.addProject.requiredFields', { defaultValue: 'برجاء إدخال الاسم بالعربية' }), { rtl: isRTL });
            return;
        }
        try {
            setIsSubmittingProject(true);
            const payload = {
                ...projectForm,
                contract_value: projectForm.contract_value ? Number(projectForm.contract_value) : undefined,
            };
            isSubOperationRef.current = true;
            const result = await dispatch(addProject(payload)).unwrap();
            if (result?.data?.id) {
                setForm(prev => ({ ...prev, project_id: result.data.id }));
                await dispatch(getProjects({ page: 1, per_page: 10 }));
                setShowAddProjectModal(false);
                setProjectForm({
                    code: '',
                    name_ar: '',
                    name_en: '',
                    customer_id: null,
                    start_date: '',
                    end_date: '',
                    status: 'planned',
                    contract_value: '',
                    tax_id: null,
                    cost_center_id: null,
                    notes: '',
                });
            }
        } catch (error) {
            // Error handled by effect
        } finally {
            setIsSubmittingProject(false);
        }
    };

    const handleAddCustomer = async (e) => {
        e.preventDefault();
        if (!customerForm.name_en) {
            toast.error(t('customers.add.nameEnRequired', { defaultValue: 'الاسم بالإنجليزية مطلوب' }), { rtl: isRTL });
            return;
        }
        if (!customerForm.name_ar) {
            toast.error(t('customers.add.nameArRequired', { defaultValue: 'الاسم بالعربية مطلوب' }), { rtl: isRTL });
            return;
        }
        try {
            setIsSubmittingCustomer(true);
            isSubOperationRef.current = true;
            const result = await dispatch(addCustomer({ ...customerForm, is_active: customerForm.is_active ? 1 : 0 })).unwrap();
            if (result) {
                setProjectForm(prev => ({ ...prev, customer_id: result.data.id }));
                // Refresh customer list
                await dispatch(getCustomers({ page: 1, per_page: 10 }));
                setShowAddCustomerModal(false);
                setCustomerForm({
                    name_en: '',
                    name_ar: '',
                    code: '',
                    contact_person: '',
                    email: '',
                    phone: '',
                    address: '',
                    tax_number: '',
                    is_active: true,
                    notes: ''
                });
            }
        } catch (error) {
            // Error handled by effect
        } finally {
            setIsSubmittingCustomer(false);
        }
    };

    const handleAddTax = async (e) => {
        e.preventDefault();
        if (!taxForm.name_ar || !taxForm.name_en || !taxForm.rate) {
            toast.error(t('quotations.add.modals.addTax.requiredFields', { defaultValue: 'برجاء استكمال الحقول المطلوبة' }), { rtl: isRTL });
            return;
        }
        try {
            const payload = { ...taxForm, rate: Number(taxForm.rate) };
            isSubOperationRef.current = true;
            const result = await dispatch(addTax(payload)).unwrap();
            if (result?.data?.id) {
                setProjectForm(prev => ({ ...prev, tax_id: result.data.id }));
                await dispatch(getTaxes({ page: 1, per_page: 10 }));
                setShowAddTaxModal(false);
                setTaxForm({ name_ar: '', name_en: '', rate: '', is_active: true });
            }
        } catch (error) {
            // Error handled by effect
        }
    };

    const handleAddCostCenter = async (e) => {
        e.preventDefault();
        if (!costCenterForm.name_ar || !costCenterForm.name_en) {
            toast.error(t('quotations.add.modals.addCostCenter.requiredFields', { defaultValue: 'برجاء استكمال الحقول المطلوبة' }), { rtl: isRTL });
            return;
        }
        try {
            isSubOperationRef.current = true;
            const result = await dispatch(addCostCenter(costCenterForm)).unwrap();
            if (result?.data?.id) {
                setProjectForm(prev => ({ ...prev, cost_center_id: result.data.id }));
                await dispatch(getCostCenters({ page: 1, per_page: 10 }));
                setShowAddCostCenterModal(false);
                setCostCenterForm({ name_ar: '', name_en: '', code: '' });
            }
        } catch (error) {
            // Error handled by effect
        }
    };

    const renderVendorDisplay = (vendorId) => {
        if (!vendorId) return t('notesPayable333.form.selectVendor');
        const selected = (vendors || []).find((v) => String(v.id) === String(vendorId));
        if (!selected) return t('notesPayable333.form.selectVendor');
        return `${selected.code ? `${selected.code} - ` : ''}${isRTL ? (selected.name_ar || selected.name || '') : (selected.name_en || selected.name_ar || selected.name || '')}`;
    };

    const renderProjectDisplay = (projectId) => {
        if (!projectId) return t('notesReceivable.form.selectProject', { defaultValue: 'اختر المشروع' });
        const selected = (projects || []).find((p) => String(p.id) === String(projectId));
        return selected ? renderDropdownItemLabel(selected, 'code', 'name_ar', 'name_en') : t('notesReceivable.form.selectProject', { defaultValue: 'اختر المشروع' });
    };

    const renderDropdownItemLabel = (item, codeField, nameArField, nameEnField) => {
        const code = item[codeField] || '';
        const name = isRTL
            ? (item[nameArField] || item[nameEnField] || '')
            : (item[nameEnField] || item[nameArField] || '');
        return `${code ? `${code} - ` : ''}${name}`;
    };

    return (
        <div style={{ padding: '30px', backgroundColor: '#14263b', minHeight: (vendorDropdownOpen || projectDropdownOpen) ? 'calc(100vh + 400px)' : 'calc(100vh - 80px)', color: 'white', direction: dir }}>
            <h1 style={{ marginTop: 0, marginBottom: 16, fontSize: '20px', textAlign }}>{t('notesPayable333.edit.title')}</h1>

            <form onSubmit={handleSubmit} style={{ backgroundColor: '#1b3350', border: '1px solid #333', borderRadius: 12, padding: 24, display: 'grid', gap: 16 }}>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 16 }}>
                    {/* Vendor Selector */}
                    <div style={{ position: 'relative' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                            <label style={{ display: 'block', fontSize: 14, textAlign, flex: 1 }}>
                                {t('notesPayable333.form.vendor')} <span style={{ color: '#dc3545' }}>*</span>
                            </label>
                            <button
                                type="button"
                                onClick={(e) => { e.stopPropagation(); e.preventDefault(); setShowAddVendorModal(true); }}
                                style={{ backgroundColor: '#AC2000', color: 'white', border: 'none', borderRadius: 6, padding: '4px 8px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4, fontSize: 11 }}
                            >
                                <FaPlus /> {t('common.add', { defaultValue: 'إضافة' })}
                            </button>
                        </div>
                        <div data-dropdown-container style={{ position: 'relative' }}>
                            <div onClick={() => { setProjectDropdownOpen(false); setVendorDropdownOpen(!vendorDropdownOpen); }} style={{ width: '100%', padding: 12, backgroundColor: '#1a1f2e', border: '1px solid #333', borderRadius: 8, color: 'white', fontSize: 14, display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer', boxSizing: 'border-box' }}>
                                <span>{renderVendorDisplay(form.vendor_id)}</span>
                                <span style={{ fontSize: 12 }}>▼</span>
                            </div>
                            {vendorDropdownOpen && (
                                <DropdownWithSearch
                                    isOpen={vendorDropdownOpen}
                                    onToggle={setVendorDropdownOpen}
                                    searchTerm={vendorSearchTerm}
                                    onSearchChange={setVendorSearchTerm}
                                    searchInputRef={vendorSearchInputRef}
                                    items={vendors || []}
                                    pagination={vendorPaginationSafe}
                                    currentPage={vendorCurrentPage}
                                    onPageChange={(page) => {
                                        setVendorCurrentPage(page);
                                        dispatch(getVendors({ page, per_page: 10, search: vendorSearchTerm || undefined }));
                                    }}
                                    onSelect={(id) => { setForm(prev => ({ ...prev, vendor_id: id })); setVendorDropdownOpen(false); }}
                                    label={t('notesPayable333.form.vendor')}
                                />
                            )}
                        </div>
                    </div>

                    {/* Project Selector */}
                    <div style={{ position: 'relative' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                            <label style={{ display: 'block', fontSize: 14, textAlign, flex: 1 }}>
                                {t('notesReceivable333.form.project', { defaultValue: 'المشروع' })}
                            </label>
                            <button
                                type="button"
                                onClick={(e) => { e.stopPropagation(); e.preventDefault(); setShowAddProjectModal(true); }}
                                style={{ backgroundColor: '#AC2000', color: 'white', border: 'none', borderRadius: 6, padding: '4px 8px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4, fontSize: 11 }}
                            >
                                <FaPlus /> {t('common.add', { defaultValue: 'إضافة' })}
                            </button>
                        </div>
                        <div data-dropdown-container style={{ position: 'relative' }}>
                            <div onClick={() => { setVendorDropdownOpen(false); setProjectDropdownOpen(!projectDropdownOpen); }} style={{ width: '100%', padding: 12, backgroundColor: '#1a1f2e', border: '1px solid #333', borderRadius: 8, color: 'white', fontSize: 14, display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer', boxSizing: 'border-box' }}>
                                <span>{renderProjectDisplay(form.project_id)}</span>
                                <span style={{ fontSize: 12 }}>▼</span>
                            </div>
                            {projectDropdownOpen && (
                                <>
                                    <DropdownWithSearch
                                        isOpen={projectDropdownOpen}
                                        onToggle={setProjectDropdownOpen}
                                        searchTerm={projectSearchTerm}
                                        onSearchChange={setProjectSearchTerm}
                                        searchInputRef={projectSearchInputRef}
                                        items={projects || []}
                                        pagination={projectsPagination}
                                        currentPage={projectCurrentPage}
                                        onPageChange={setProjectCurrentPage}
                                        onSelect={(id) => { setForm(prev => ({ ...prev, project_id: id })); setProjectDropdownOpen(false); }}
                                        label={t('notesReceivable333.form.project')}
                                    />
                                </>
                            )}
                        </div>
                    </div>

                    {/* Amount */}
                    <div>
                        <label style={{ display: 'block', marginBottom: 6, fontSize: 14, textAlign }}>
                            {t('notesPayable333.form.amount')} <span style={{ color: '#AC2000' }}>*</span>
                        </label>
                        <input type="number" name="amount" value={form.amount} onChange={handleChange} required style={{ width: '100%', padding: 12, backgroundColor: '#1a1f2e', border: '1px solid #333', borderRadius: 8, color: 'white', fontSize: 14, direction: dir, textAlign }} />
                    </div>

                    {/* Issue Date */}
                    <div>
                        <label style={{ display: 'block', marginBottom: 6, fontSize: 14, textAlign }}>{t('notesPayable333.form.issueDate')}</label>
                        <input type="date" name="issue_date" value={form.issue_date} onChange={handleChange} style={{ width: '100%', padding: 12, backgroundColor: '#1a1f2e', border: '1px solid #333', borderRadius: 8, color: 'white', fontSize: 14, direction: dir, textAlign }} />
                    </div>
                    {/* Due Date */}
                    <div>
                        <label style={{ display: 'block', marginBottom: 6, fontSize: 14, textAlign }}>{t('notesPayable333.form.dueDate')}</label>
                        <input type="date" name="due_date" value={form.due_date} onChange={handleChange} style={{ width: '100%', padding: 12, backgroundColor: '#1a1f2e', border: '1px solid #333', borderRadius: 8, color: 'white', fontSize: 14, direction: dir, textAlign }} />
                    </div>

                    {/* Note Number */}
                    <div>
                        <label style={{ display: 'block', marginBottom: 6, fontSize: 14, textAlign }}>{t('notesPayable333.form.noteNumber')}</label>
                        <input type="text" name="note_number" value={form.note_number} onChange={handleChange} style={{ width: '100%', padding: 12, backgroundColor: '#1a1f2e', border: '1px solid #333', borderRadius: 8, color: 'white', fontSize: 14, direction: dir, textAlign }} />
                    </div>
                </div>

                {/* Description */}
                <div style={{ gridColumn: '1 / -1' }}>
                    <label style={{ display: 'block', marginBottom: 6, fontSize: 14, textAlign }}>{t('notesPayable333.form.description')}</label>
                    <textarea name="description" value={form.description} onChange={handleChange} rows={3} style={{ width: '100%', padding: 12, backgroundColor: '#1a1f2e', border: '1px solid #333', borderRadius: 8, color: 'white', fontSize: 14, resize: 'vertical', direction: dir, textAlign }} />
                </div>

                {/* Footer Buttons */}
                <div style={{ display: 'flex', justifyContent: 'center', gap: 12, marginTop: 10 }}>
                    <button type="button" onClick={() => navigate('/notes-payable')} style={{ backgroundColor: '#666', color: 'white', border: 'none', borderRadius: 8, padding: '10px 24px', cursor: 'pointer' }}>
                        {t('notesPayable333.form.cancel')}
                    </button>
                    <button type="submit" disabled={isSubmitting} style={{ backgroundColor: '#AC2000', color: 'white', border: 'none', borderRadius: 8, padding: '10px 24px', cursor: 'pointer', opacity: isSubmitting ? 0.7 : 1 }}>
                        {isSubmitting ? t('common.loading') : t('notesPayable333.form.save')}
                    </button>
                </div>
            </form>

            {/* Modals */}
            {showAddVendorModal && createPortal(
                <div
                    style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: 'rgba(0, 0, 0, 0.7)',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        zIndex: 999999,
                        padding: '20px'
                    }}
                    onClick={() => setShowAddVendorModal(false)}
                >
                    <div
                        style={{
                            backgroundColor: '#1b3350',
                            borderRadius: '12px',
                            padding: '30px',
                            maxWidth: '800px',
                            width: '100%',
                            maxHeight: '90vh',
                            overflowY: 'auto',
                            border: '1px solid #333',
                            direction: isRTL ? 'rtl' : 'ltr'
                        }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexDirection: isRTL ? 'row-reverse' : 'row' }}>
                            <h2 style={{ margin: 0, fontSize: '20px', color: 'white', textAlign: isRTL ? 'right' : 'left' }}>
                                {t('vendors.add.title', { defaultValue: 'إضافة مورد جديد' })}
                            </h2>
                            <button
                                onClick={() => setShowAddVendorModal(false)}
                                style={{
                                    backgroundColor: 'transparent',
                                    border: 'none',
                                    color: 'white',
                                    fontSize: '24px',
                                    cursor: 'pointer',
                                    padding: '0',
                                    width: '30px',
                                    height: '30px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                }}
                            >
                                <FaTimes />
                            </button>
                        </div>
                        <form onSubmit={handleAddVendor}>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '20px' }}>
                                <div>
                                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: 'white', fontSize: '14px', textAlign: isRTL ? 'right' : 'left' }}>
                                        {t('vendors.add.nameAr')} <span style={{ color: '#dc3545' }}>*</span>
                                    </label>
                                    <input
                                        type="text"
                                        name="name_ar"
                                        value={vendorForm.name_ar}
                                        onChange={handleVendorFormChange}
                                        required
                                        style={{
                                            width: '100%',
                                            padding: '12px 15px',
                                            backgroundColor: '#1a1f2e',
                                            border: '1px solid #333',
                                            borderRadius: '8px',
                                            color: 'white',
                                            fontSize: '14px',
                                            direction: 'rtl',
                                            textAlign: 'right'
                                        }}
                                    />
                                </div>
                                <div>
                                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: 'white', fontSize: '14px', textAlign: isRTL ? 'right' : 'left' }}>
                                        {t('vendors.add.nameEn')} <span style={{ color: '#dc3545' }}>*</span>
                                    </label>
                                    <input
                                        type="text"
                                        name="name_en"
                                        value={vendorForm.name_en}
                                        onChange={handleVendorFormChange}
                                        required
                                        style={{
                                            width: '100%',
                                            padding: '12px 15px',
                                            backgroundColor: '#1a1f2e',
                                            border: '1px solid #333',
                                            borderRadius: '8px',
                                            color: 'white',
                                            fontSize: '14px',
                                            direction: 'ltr',
                                            textAlign: 'left'
                                        }}
                                    />
                                </div>
                                <div>
                                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: 'white', fontSize: '14px', textAlign: isRTL ? 'right' : 'left' }}>
                                        {t('vendors.add.contactPerson')}
                                    </label>
                                    <input
                                        type="text"
                                        name="contact_person"
                                        value={vendorForm.contact_person}
                                        onChange={handleVendorFormChange}
                                        style={{
                                            width: '100%',
                                            padding: '12px 15px',
                                            backgroundColor: '#1a1f2e',
                                            border: '1px solid #333',
                                            borderRadius: '8px',
                                            color: 'white',
                                            fontSize: '14px',
                                            direction: isRTL ? 'rtl' : 'ltr',
                                            textAlign: isRTL ? 'right' : 'left'
                                        }}
                                    />
                                </div>
                                <div>
                                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: 'white', fontSize: '14px', textAlign: isRTL ? 'right' : 'left' }}>
                                        {t('vendors.add.email')}
                                    </label>
                                    <input
                                        type="email"
                                        name="email"
                                        value={vendorForm.email}
                                        onChange={handleVendorFormChange}
                                        style={{
                                            width: '100%',
                                            padding: '12px 15px',
                                            backgroundColor: '#1a1f2e',
                                            border: '1px solid #333',
                                            borderRadius: '8px',
                                            color: 'white',
                                            fontSize: '14px',
                                            direction: 'ltr',
                                            textAlign: 'left'
                                        }}
                                    />
                                </div>
                                <div>
                                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: 'white', fontSize: '14px', textAlign: isRTL ? 'right' : 'left' }}>
                                        {t('vendors.add.phone')}
                                    </label>
                                    <input
                                        type="text"
                                        name="phone"
                                        value={vendorForm.phone}
                                        onChange={handleVendorFormChange}
                                        style={{
                                            width: '100%',
                                            padding: '12px 15px',
                                            backgroundColor: '#1a1f2e',
                                            border: '1px solid #333',
                                            borderRadius: '8px',
                                            color: 'white',
                                            fontSize: '14px',
                                            direction: isRTL ? 'rtl' : 'ltr',
                                            textAlign: isRTL ? 'right' : 'left'
                                        }}
                                    />
                                </div>
                                <div>
                                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: 'white', fontSize: '14px', textAlign: isRTL ? 'right' : 'left' }}>
                                        {t('vendors.add.taxNumber')}
                                    </label>
                                    <input
                                        type="text"
                                        name="tax_number"
                                        value={vendorForm.tax_number}
                                        onChange={handleVendorFormChange}
                                        style={{
                                            width: '100%',
                                            padding: '12px 15px',
                                            backgroundColor: '#1a1f2e',
                                            border: '1px solid #333',
                                            borderRadius: '8px',
                                            color: 'white',
                                            fontSize: '14px',
                                            direction: isRTL ? 'rtl' : 'ltr',
                                            textAlign: isRTL ? 'right' : 'left'
                                        }}
                                    />
                                </div>
                                <div style={{ gridColumn: '1 / -1' }}>
                                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: 'white', fontSize: '14px', textAlign: isRTL ? 'right' : 'left' }}>
                                        {t('vendors.add.address')}
                                    </label>
                                    <input
                                        type="text"
                                        name="address"
                                        value={vendorForm.address}
                                        onChange={handleVendorFormChange}
                                        style={{
                                            width: '100%',
                                            padding: '12px 15px',
                                            backgroundColor: '#1a1f2e',
                                            border: '1px solid #333',
                                            borderRadius: '8px',
                                            color: 'white',
                                            fontSize: '14px',
                                            direction: isRTL ? 'rtl' : 'ltr',
                                            textAlign: isRTL ? 'right' : 'left'
                                        }}
                                    />
                                </div>
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'center', gap: '15px', marginTop: '30px' }}>
                                <button
                                    type="button"
                                    onClick={() => setShowAddVendorModal(false)}
                                    style={{ backgroundColor: '#6c757d', color: 'white', border: 'none', padding: '12px 25px', borderRadius: '8px', fontSize: '14px', fontWeight: 'bold', cursor: 'pointer' }}
                                >
                                    {t('common.cancel', { defaultValue: 'إلغاء' })}
                                </button>
                                <button
                                    type="submit"
                                    disabled={isSubmittingVendor}
                                    style={{ backgroundColor: '#AC2000', color: 'white', border: 'none', padding: '12px 25px', borderRadius: '8px', fontSize: '14px', fontWeight: 'bold', cursor: isSubmittingVendor ? 'not-allowed' : 'pointer', opacity: isSubmittingVendor ? 0.7 : 1 }}
                                >
                                    {isSubmittingVendor ? t('common.saving', { defaultValue: 'جاري الحفظ...' }) : t('common.save', { defaultValue: 'حفظ' })}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>,
                document.body
            )}

            {/* Add Project Modal */}
            {showAddProjectModal && createPortal(
                <div
                    style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: 'rgba(0, 0, 0, 0.7)',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        zIndex: 999999,
                        padding: '20px'
                    }}
                    onClick={() => setShowAddProjectModal(false)}
                >
                    <div
                        style={{
                            backgroundColor: '#1b3350',
                            borderRadius: '12px',
                            padding: '30px',
                            maxWidth: '900px',
                            width: '100%',
                            maxHeight: '90vh',
                            overflowY: 'auto',
                            border: '1px solid #333',
                            direction: isRTL ? 'rtl' : 'ltr'
                        }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexDirection: isRTL ? 'row-reverse' : 'row' }}>
                            <h2 style={{ margin: 0, fontSize: '20px', color: 'white', textAlign: isRTL ? 'right' : 'left' }}>{t('quotations.add.modals.addProject.title', { defaultValue: 'إضافة مشروع جديد' })}</h2>
                            <button
                                onClick={() => setShowAddProjectModal(false)}
                                style={{
                                    backgroundColor: 'transparent',
                                    border: 'none',
                                    color: 'white',
                                    fontSize: '24px',
                                    cursor: 'pointer',
                                    padding: '0',
                                    width: '30px',
                                    height: '30px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                }}
                            >
                                <FaTimes />
                            </button>
                        </div>
                        <form onSubmit={handleAddProject}>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '20px' }}>
                                <div>
                                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: 'white', fontSize: '14px', direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>
                                        {t('quotations.add.modals.addProject.nameAr', { defaultValue: 'الاسم بالعربية' })} <span style={{ color: '#dc3545' }}>*</span>
                                    </label>
                                    <input
                                        type="text"
                                        name="name_ar"
                                        value={projectForm.name_ar}
                                        onChange={handleProjectFormChange}
                                        required
                                        style={{
                                            width: '100%',
                                            padding: '12px 15px',
                                            backgroundColor: '#1a1f2e',
                                            border: '1px solid #333',
                                            borderRadius: '8px',
                                            color: 'white',
                                            fontSize: '14px',
                                            direction: isRTL ? 'rtl' : 'ltr',
                                            textAlign: isRTL ? 'right' : 'left'
                                        }}
                                    />
                                </div>
                                <div>
                                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: 'white', fontSize: '14px', direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>
                                        {t('quotations.add.modals.addProject.nameEn', { defaultValue: 'الاسم بالإنجليزية' })}
                                    </label>
                                    <input
                                        type="text"
                                        name="name_en"
                                        value={projectForm.name_en}
                                        onChange={handleProjectFormChange}
                                        style={{
                                            width: '100%',
                                            padding: '12px 15px',
                                            backgroundColor: '#1a1f2e',
                                            border: '1px solid #333',
                                            borderRadius: '8px',
                                            color: 'white',
                                            fontSize: '14px',
                                            direction: isRTL ? 'rtl' : 'ltr',
                                            textAlign: isRTL ? 'right' : 'left'
                                        }}
                                    />
                                </div>
                                <div>
                                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: 'white', fontSize: '14px', direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>
                                        {t('quotations.add.modals.addProject.code', { defaultValue: 'كود المشروع' })}
                                    </label>
                                    <input
                                        type="text"
                                        name="code"
                                        value={projectForm.code}
                                        onChange={handleProjectFormChange}
                                        style={{
                                            width: '100%',
                                            padding: '12px 15px',
                                            backgroundColor: '#1a1f2e',
                                            border: '1px solid #333',
                                            borderRadius: '8px',
                                            color: 'white',
                                            fontSize: '14px',
                                            direction: isRTL ? 'rtl' : 'ltr',
                                            textAlign: isRTL ? 'right' : 'left'
                                        }}
                                    />
                                </div>

                                <div style={{ position: 'relative' }} data-dropdown-container>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                                        <label style={{ display: 'block', fontSize: 13, fontWeight: 'bold', color: 'white', flex: 1, textAlign: isRTL ? 'right' : 'left' }}>
                                            {t('quotations.add.modals.addProject.customer', { defaultValue: 'العميل' })}
                                        </label>
                                        {/* Add Customer Button inside Project Modal - Keeping logic simple/stubbed as requested */}
                                        <button
                                            type="button"
                                            onClick={(e) => { e.stopPropagation(); setShowAddCustomerModal(true); }}
                                            style={{ backgroundColor: '#AC2000', color: 'white', border: 'none', borderRadius: 6, padding: '4px 8px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4, fontSize: 11 }}
                                        >
                                            <FaPlus /> {t('common.add', { defaultValue: 'إضافة' })}
                                        </button>
                                    </div>
                                    <div
                                        data-dropdown-toggle
                                        onClick={() => {
                                            setTaxDropdownOpen(false);
                                            setCostCenterDropdownOpen(false);
                                            setProjModalCustomerDropdownOpen((p) => !p);
                                        }}
                                        style={{ flex: 1, padding: 12, backgroundColor: '#1a1f2e', border: '1px solid #333', borderRadius: 8, color: 'white', fontSize: 14, display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer', boxSizing: 'border-box' }}
                                    >
                                        <span style={{ direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>
                                            {projectForm.customer_id ? (customers || []).find(c => String(c.id) === String(projectForm.customer_id)) ? renderDropdownItemLabel((customers || []).find(c => String(c.id) === String(projectForm.customer_id)), 'code', 'name_ar', 'name_en') : t('quotations.add.modals.addProject.selectCustomer', { defaultValue: 'اختر العميل' }) : t('quotations.add.modals.addProject.selectCustomer', { defaultValue: 'اختر العميل' })}
                                        </span>
                                        <span style={{ fontSize: 12 }}>▼</span>
                                    </div>
                                    {projModalCustomerDropdownOpen && (
                                        <DropdownWithSearch
                                            isOpen={projModalCustomerDropdownOpen}
                                            onToggle={setProjModalCustomerDropdownOpen}
                                            searchTerm={projModalCustomerSearchTerm}
                                            onSearchChange={(val) => {
                                                setProjModalCustomerSearchTerm(val);
                                                if (projModalCustomerSearchTimeoutRef.current) clearTimeout(projModalCustomerSearchTimeoutRef.current);
                                                projModalCustomerSearchTimeoutRef.current = setTimeout(() => {
                                                    setProjModalCustomerCurrentPage(1);
                                                    dispatch(getCustomers({ page: 1, per_page: 10, search: val || undefined }));
                                                }, 300);
                                            }}
                                            searchInputRef={projModalCustomerSearchInputRef}
                                            items={Array.isArray(customers) ? customers : []}
                                            pagination={customersPagination}
                                            currentPage={projModalCustomerCurrentPage}
                                            onPageChange={(page) => {
                                                setProjModalCustomerCurrentPage(page);
                                                dispatch(getCustomers({ page, per_page: 10, search: projModalCustomerSearchTerm || undefined }));
                                            }}
                                            onSelect={(id) => {
                                                setProjectForm((prev) => ({ ...prev, customer_id: id }));
                                                setProjModalCustomerDropdownOpen(false);
                                            }}
                                            label={t('quotations.add.modals.addProject.customer', { defaultValue: 'عميل' })}
                                        />
                                    )}
                                </div>

                                <div>
                                    <label style={{ display: 'block', marginBottom: 6, fontSize: 14, color: 'white', direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>{t('quotations.add.modals.addProject.startDate', { defaultValue: 'تاريخ البداية' })}</label>
                                    <input type="date" name="start_date" value={projectForm.start_date} onChange={handleProjectFormChange} style={{ width: '100%', padding: 12, backgroundColor: '#1a1f2e', border: '1px solid #333', borderRadius: 8, color: 'white', fontSize: 14, direction: isRTL ? 'rtl' : 'ltr' }} />
                                </div>
                                <div>
                                    <label style={{ display: 'block', marginBottom: 6, fontSize: 14, color: 'white', direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>{t('quotations.add.modals.addProject.endDate', { defaultValue: 'تاريخ النهاية' })}</label>
                                    <input type="date" name="end_date" value={projectForm.end_date} onChange={handleProjectFormChange} style={{ width: '100%', padding: 12, backgroundColor: '#1a1f2e', border: '1px solid #333', borderRadius: 8, color: 'white', fontSize: 14, direction: isRTL ? 'rtl' : 'ltr' }} />
                                </div>
                                <div>
                                    <label style={{ display: 'block', marginBottom: 6, fontSize: 14, color: 'white', direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>{t('quotations.add.modals.addProject.status', { defaultValue: 'الحالة' })}</label>
                                    <select name="status" value={projectForm.status} onChange={handleProjectFormChange} style={{ width: '100%', padding: 12, backgroundColor: '#1a1f2e', border: '1px solid #333', borderRadius: 8, color: 'white', fontSize: 14, direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>
                                        <option value="planned">{t('quotations.add.modals.addProject.statusOptions.planned', { defaultValue: 'مخطط' })}</option>
                                        <option value="in_progress">{t('quotations.add.modals.addProject.statusOptions.inProgress', { defaultValue: 'قيد التنفيذ' })}</option>
                                        <option value="paused">{t('quotations.add.modals.addProject.statusOptions.paused', { defaultValue: 'متوقف' })}</option>
                                        <option value="completed">{t('quotations.add.modals.addProject.statusOptions.completed', { defaultValue: 'مكتمل' })}</option>
                                        <option value="cancelled">{t('quotations.add.modals.addProject.statusOptions.cancelled', { defaultValue: 'ملغى' })}</option>
                                    </select>
                                </div>
                                <div>
                                    <label style={{ display: 'block', marginBottom: 6, fontSize: 14, color: 'white', direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>{t('quotations.add.modals.addProject.contractValue', { defaultValue: 'قيمة العقد' })}</label>
                                    <input type="number" name="contract_value" value={projectForm.contract_value} onChange={handleProjectFormChange} style={{ width: '100%', padding: 12, backgroundColor: '#1a1f2e', border: '1px solid #333', borderRadius: 8, color: 'white', fontSize: 14, direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }} />
                                </div>

                                <div style={{ position: 'relative' }} data-dropdown-container>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                                        <label style={{ display: 'block', fontSize: 13, fontWeight: 'bold', color: 'white', flex: 1, textAlign: isRTL ? 'right' : 'left' }}>
                                            {t('quotations.add.modals.addProject.tax', { defaultValue: 'الضريبة' })}
                                        </label>
                                        <button
                                            type="button"
                                            onClick={(e) => { e.stopPropagation(); setShowAddTaxModal(true); }}
                                            style={{ backgroundColor: '#AC2000', color: 'white', border: 'none', borderRadius: 6, padding: '4px 8px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4, fontSize: 11 }}
                                        >
                                            <FaPlus /> {t('common.add', { defaultValue: 'إضافة' })}
                                        </button>
                                    </div>
                                    <div
                                        data-dropdown-toggle
                                        onClick={() => {
                                            setProjModalCustomerDropdownOpen(false);
                                            setCostCenterDropdownOpen(false);
                                            if (taxDropdownOpen) { setTaxSearchTerm(''); setTaxCurrentPage(1); }
                                            setTaxDropdownOpen((p) => !p);
                                        }}
                                        style={{ width: '100%', padding: '12px 15px', backgroundColor: '#1a1f2e', border: '1px solid #333', borderRadius: 8, color: 'white', fontSize: 14, display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer', boxSizing: 'border-box' }}
                                    >
                                        <span>{projectForm.tax_id ? renderDropdownItemLabel((taxes || []).find(t => t.id === projectForm.tax_id) || {}, '', 'name_ar', 'name_en') : t('quotations.add.selectTax', { defaultValue: 'اختر الضريبة' })}</span>
                                        <span style={{ fontSize: 12 }}>▼</span>
                                    </div>
                                    {taxDropdownOpen && (
                                        <DropdownWithSearch
                                            isOpen={taxDropdownOpen}
                                            onToggle={setTaxDropdownOpen}
                                            searchTerm={taxSearchTerm}
                                            onSearchChange={setTaxSearchTerm}
                                            searchInputRef={taxSearchInputRef}
                                            items={taxes || []}
                                            pagination={taxesPagination}
                                            currentPage={taxCurrentPage}
                                            onPageChange={setTaxCurrentPage}
                                            onSelect={(id) => {
                                                setProjectForm(prev => ({ ...prev, tax_id: id }));
                                                setTaxDropdownOpen(false);
                                            }}
                                            label={t('quotations.add.modals.addProject.tax', { defaultValue: 'الضريبة' })}
                                        />
                                    )}
                                </div>
                                <div style={{ position: 'relative' }} data-dropdown-container>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                                        <label style={{ display: 'block', fontSize: 13, fontWeight: 'bold', color: 'white', flex: 1, textAlign: isRTL ? 'right' : 'left' }}>
                                            {t('quotations.add.modals.addProject.costCenter', { defaultValue: 'مركز التكلفة' })}
                                        </label>
                                        <button
                                            type="button"
                                            onClick={(e) => { e.stopPropagation(); setShowAddCostCenterModal(true); }}
                                            style={{ backgroundColor: '#AC2000', color: 'white', border: 'none', borderRadius: 6, padding: '4px 8px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4, fontSize: 11 }}
                                        >
                                            <FaPlus /> {t('common.add', { defaultValue: 'إضافة' })}
                                        </button>
                                    </div>
                                    <div
                                        data-dropdown-toggle
                                        onClick={() => {
                                            setProjModalCustomerDropdownOpen(false);
                                            setTaxDropdownOpen(false);
                                            if (costCenterDropdownOpen) { setCostCenterSearchTerm(''); setCostCenterCurrentPage(1); }
                                            setCostCenterDropdownOpen((p) => !p);
                                        }}
                                        style={{ width: '100%', padding: '12px 15px', backgroundColor: '#1a1f2e', border: '1px solid #333', borderRadius: 8, color: 'white', fontSize: 14, display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer', boxSizing: 'border-box' }}
                                    >
                                        <span>{projectForm.cost_center_id ? renderDropdownItemLabel((costCenters || []).find(c => c.id === projectForm.cost_center_id) || {}, 'code', 'name_ar', 'name_en') : t('quotations.add.selectCostCenter', { defaultValue: 'اختر مركز التكلفة' })}</span>
                                        <span style={{ fontSize: 12 }}>▼</span>
                                    </div>
                                    {costCenterDropdownOpen && (
                                        <DropdownWithSearch
                                            isOpen={costCenterDropdownOpen}
                                            onToggle={setCostCenterDropdownOpen}
                                            searchTerm={costCenterSearchTerm}
                                            onSearchChange={setCostCenterSearchTerm}
                                            searchInputRef={costCenterSearchInputRef}
                                            items={costCenters || []}
                                            pagination={costCentersPagination}
                                            currentPage={costCenterCurrentPage}
                                            onPageChange={setCostCenterCurrentPage}
                                            onSelect={(id) => {
                                                setProjectForm(prev => ({ ...prev, cost_center_id: id }));
                                                setCostCenterDropdownOpen(false);
                                            }}
                                            label={t('quotations.add.modals.addProject.costCenter', { defaultValue: 'مركز التكلفة' })}
                                        />
                                    )}
                                </div>
                            </div>
                            <div style={{ marginTop: '20px' }}>
                                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: 'white', fontSize: '14px', direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>
                                    {t('quotations.add.modals.addProject.notes', { defaultValue: 'ملاحظات' })}
                                </label>
                                <textarea
                                    ref={projectNotesTextareaRef}
                                    name="notes"
                                    value={projectForm.notes}
                                    onChange={handleProjectFormChange}
                                    rows={3}
                                    style={{
                                        width: '100%',
                                        padding: '12px 15px',
                                        backgroundColor: '#1a1f2e',
                                        border: '1px solid #333',
                                        borderRadius: '8px',
                                        color: 'white',
                                        fontSize: '14px',
                                        resize: 'vertical',
                                        direction: isRTL ? 'rtl' : 'ltr',
                                        textAlign: isRTL ? 'right' : 'left'
                                    }}
                                />
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'center', gap: '15px', marginTop: '30px' }}>
                                <button
                                    type="button"
                                    onClick={() => setShowAddProjectModal(false)}
                                    style={{ backgroundColor: '#6c757d', color: 'white', border: 'none', padding: '12px 25px', borderRadius: '8px', fontSize: '14px', fontWeight: 'bold', cursor: 'pointer' }}
                                >
                                    {t('common.cancel', { defaultValue: 'إلغاء' })}
                                </button>
                                <button
                                    type="submit"
                                    disabled={isSubmittingProject}
                                    style={{ backgroundColor: '#AC2000', color: 'white', border: 'none', padding: '12px 25px', borderRadius: '8px', fontSize: '14px', fontWeight: 'bold', cursor: isSubmittingProject ? 'not-allowed' : 'pointer', opacity: isSubmittingProject ? 0.7 : 1 }}
                                >
                                    {isSubmittingProject ? t('common.saving', { defaultValue: 'جاري الحفظ...' }) : t('common.save', { defaultValue: 'حفظ' })}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>,
                document.body
            )}

            {showAddCustomerModal && createPortal(
                <div
                    style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: 'rgba(0, 0, 0, 0.7)',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        zIndex: 999999,
                        padding: '20px'
                    }}
                    onClick={() => setShowAddCustomerModal(false)}
                >
                    <div
                        style={{
                            backgroundColor: '#1b3350',
                            borderRadius: '12px',
                            padding: '30px',
                            maxWidth: '800px',
                            width: '100%',
                            maxHeight: '90vh',
                            overflowY: 'auto',
                            border: '1px solid #333',
                            direction: isRTL ? 'rtl' : 'ltr'
                        }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexDirection: isRTL ? 'row-reverse' : 'row' }}>
                            <h2 style={{ margin: 0, fontSize: '20px', color: 'white', textAlign: isRTL ? 'right' : 'left' }}>
                                {t('customers.add.title', { defaultValue: 'إضافة عميل جديد' })}
                            </h2>
                            <button
                                onClick={() => setShowAddCustomerModal(false)}
                                style={{
                                    backgroundColor: 'transparent',
                                    border: 'none',
                                    color: 'white',
                                    fontSize: '24px',
                                    cursor: 'pointer',
                                    padding: '0',
                                    width: '30px',
                                    height: '30px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                }}
                            >
                                <FaTimes />
                            </button>
                        </div>
                        <form onSubmit={handleAddCustomer}>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '20px' }}>
                                <div>
                                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: 'white', fontSize: '14px', textAlign: isRTL ? 'right' : 'left' }}>
                                        {t('customers.add.nameAr', { defaultValue: 'الاسم بالعربية' })} <span style={{ color: '#dc3545' }}>*</span>
                                    </label>
                                    <input
                                        type="text"
                                        name="name_ar"
                                        value={customerForm.name_ar}
                                        onChange={(e) => setCustomerForm(prev => ({ ...prev, name_ar: e.target.value }))}
                                        required
                                        style={{
                                            width: '100%',
                                            padding: '12px 15px',
                                            backgroundColor: '#1a1f2e',
                                            border: '1px solid #333',
                                            borderRadius: '8px',
                                            color: 'white',
                                            fontSize: '14px',
                                            direction: 'rtl',
                                            textAlign: 'right'
                                        }}
                                    />
                                </div>
                                <div>
                                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: 'white', fontSize: '14px', textAlign: isRTL ? 'right' : 'left' }}>
                                        {t('customers.add.nameEn', { defaultValue: 'الاسم بالإنجليزية' })} <span style={{ color: '#dc3545' }}>*</span>
                                    </label>
                                    <input
                                        type="text"
                                        name="name_en"
                                        value={customerForm.name_en}
                                        onChange={(e) => setCustomerForm(prev => ({ ...prev, name_en: e.target.value }))}
                                        required
                                        style={{
                                            width: '100%',
                                            padding: '12px 15px',
                                            backgroundColor: '#1a1f2e',
                                            border: '1px solid #333',
                                            borderRadius: '8px',
                                            color: 'white',
                                            fontSize: '14px',
                                            direction: 'ltr',
                                            textAlign: 'left'
                                        }}
                                    />
                                </div>
                                <div>
                                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: 'white', fontSize: '14px', textAlign: isRTL ? 'right' : 'left' }}>
                                        {t('customers.add.code', { defaultValue: 'كود العميل' })}
                                    </label>
                                    <input
                                        type="text"
                                        name="code"
                                        value={customerForm.code}
                                        onChange={(e) => setCustomerForm(prev => ({ ...prev, code: e.target.value }))}
                                        style={{
                                            width: '100%',
                                            padding: '12px 15px',
                                            backgroundColor: '#1a1f2e',
                                            border: '1px solid #333',
                                            borderRadius: '8px',
                                            color: 'white',
                                            fontSize: '14px',
                                            direction: isRTL ? 'rtl' : 'ltr',
                                            textAlign: isRTL ? 'right' : 'left'
                                        }}
                                    />
                                </div>
                                <div>
                                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: 'white', fontSize: '14px', textAlign: isRTL ? 'right' : 'left' }}>
                                        {t('customers.add.contactPerson', { defaultValue: 'الشخص المسؤول' })}
                                    </label>
                                    <input
                                        type="text"
                                        name="contact_person"
                                        value={customerForm.contact_person}
                                        onChange={(e) => setCustomerForm(prev => ({ ...prev, contact_person: e.target.value }))}
                                        style={{
                                            width: '100%',
                                            padding: '12px 15px',
                                            backgroundColor: '#1a1f2e',
                                            border: '1px solid #333',
                                            borderRadius: '8px',
                                            color: 'white',
                                            fontSize: '14px',
                                            direction: isRTL ? 'rtl' : 'ltr',
                                            textAlign: isRTL ? 'right' : 'left'
                                        }}
                                    />
                                </div>
                                <div>
                                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: 'white', fontSize: '14px', textAlign: isRTL ? 'right' : 'left' }}>
                                        {t('customers.add.email', { defaultValue: 'البريد الإلكتروني' })}
                                    </label>
                                    <input
                                        type="email"
                                        name="email"
                                        value={customerForm.email}
                                        onChange={(e) => setCustomerForm(prev => ({ ...prev, email: e.target.value }))}
                                        style={{
                                            width: '100%',
                                            padding: '12px 15px',
                                            backgroundColor: '#1a1f2e',
                                            border: '1px solid #333',
                                            borderRadius: '8px',
                                            color: 'white',
                                            fontSize: '14px',
                                            direction: 'ltr',
                                            textAlign: 'left'
                                        }}
                                    />
                                </div>
                                <div>
                                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: 'white', fontSize: '14px', textAlign: isRTL ? 'right' : 'left' }}>
                                        {t('customers.add.phone', { defaultValue: 'رقم الهاتف' })}
                                    </label>
                                    <input
                                        type="text"
                                        name="phone"
                                        value={customerForm.phone}
                                        onChange={(e) => setCustomerForm(prev => ({ ...prev, phone: e.target.value }))}
                                        style={{
                                            width: '100%',
                                            padding: '12px 15px',
                                            backgroundColor: '#1a1f2e',
                                            border: '1px solid #333',
                                            borderRadius: '8px',
                                            color: 'white',
                                            fontSize: '14px',
                                            direction: isRTL ? 'rtl' : 'ltr',
                                            textAlign: isRTL ? 'right' : 'left'
                                        }}
                                    />
                                </div>
                                <div>
                                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: 'white', fontSize: '14px', textAlign: isRTL ? 'right' : 'left' }}>
                                        {t('customers.add.taxNumber', { defaultValue: 'الرقم الضريبي' })}
                                    </label>
                                    <input
                                        type="text"
                                        name="tax_number"
                                        value={customerForm.tax_number}
                                        onChange={(e) => setCustomerForm(prev => ({ ...prev, tax_number: e.target.value }))}
                                        style={{
                                            width: '100%',
                                            padding: '12px 15px',
                                            backgroundColor: '#1a1f2e',
                                            border: '1px solid #333',
                                            borderRadius: '8px',
                                            color: 'white',
                                            fontSize: '14px',
                                            direction: isRTL ? 'rtl' : 'ltr',
                                            textAlign: isRTL ? 'right' : 'left'
                                        }}
                                    />
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <input
                                        type="checkbox"
                                        name="is_active"
                                        checked={customerForm.is_active}
                                        onChange={(e) => setCustomerForm(prev => ({ ...prev, is_active: e.target.checked }))}
                                        style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                                    />
                                    <label style={{ color: 'white', fontSize: '14px', cursor: 'pointer' }}>
                                        {t('customers.add.status.isActive', { defaultValue: 'نشط' })}
                                    </label>
                                </div>
                            </div>
                            <div style={{ marginTop: '20px' }}>
                                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: 'white', fontSize: '14px', textAlign: isRTL ? 'right' : 'left' }}>
                                    {t('customers.add.address', { defaultValue: 'العنوان' })}
                                </label>
                                <input
                                    type="text"
                                    name="address"
                                    value={customerForm.address}
                                    onChange={(e) => setCustomerForm(prev => ({ ...prev, address: e.target.value }))}
                                    style={{
                                        width: '100%',
                                        padding: '12px 15px',
                                        backgroundColor: '#1a1f2e',
                                        border: '1px solid #333',
                                        borderRadius: '8px',
                                        color: 'white',
                                        fontSize: '14px',
                                        direction: isRTL ? 'rtl' : 'ltr',
                                        textAlign: isRTL ? 'right' : 'left'
                                    }}
                                />
                            </div>
                            <div style={{ marginTop: '20px' }}>
                                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: 'white', fontSize: '14px', textAlign: isRTL ? 'right' : 'left' }}>
                                    {t('customers.add.notes', { defaultValue: 'ملاحظات' })}
                                </label>
                                <textarea
                                    name="notes"
                                    value={customerForm.notes}
                                    onChange={(e) => setCustomerForm(prev => ({ ...prev, notes: e.target.value }))}
                                    rows={3}
                                    style={{
                                        width: '100%',
                                        padding: '12px 15px',
                                        backgroundColor: '#1a1f2e',
                                        border: '1px solid #333',
                                        borderRadius: '8px',
                                        color: 'white',
                                        fontSize: '14px',
                                        resize: 'vertical',
                                        direction: isRTL ? 'rtl' : 'ltr',
                                        textAlign: isRTL ? 'right' : 'left'
                                    }}
                                />
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'center', gap: '15px', marginTop: '30px' }}>
                                <button
                                    type="button"
                                    onClick={() => setShowAddCustomerModal(false)}
                                    style={{ backgroundColor: '#6c757d', color: 'white', border: 'none', padding: '12px 25px', borderRadius: '8px', fontSize: '14px', fontWeight: 'bold', cursor: 'pointer' }}
                                >
                                    {t('common.cancel', { defaultValue: 'إلغاء' })}
                                </button>
                                <button
                                    type="submit"
                                    disabled={isSubmittingCustomer}
                                    style={{ backgroundColor: '#AC2000', color: 'white', border: 'none', padding: '12px 25px', borderRadius: '8px', fontSize: '14px', fontWeight: 'bold', cursor: isSubmittingCustomer ? 'not-allowed' : 'pointer', opacity: isSubmittingCustomer ? 0.7 : 1 }}
                                >
                                    {isSubmittingCustomer ? t('common.saving', { defaultValue: 'جاري الحفظ...' }) : t('common.save', { defaultValue: 'حفظ' })}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
                , document.body)}

            {showAddTaxModal && createPortal(
                <div
                    style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: 'rgba(0, 0, 0, 0.7)',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        zIndex: 999999,
                        padding: '20px'
                    }}
                    onClick={() => setShowAddTaxModal(false)}
                >
                    <div
                        style={{
                            backgroundColor: '#1b3350',
                            borderRadius: '12px',
                            padding: '30px',
                            maxWidth: '500px',
                            width: '100%',
                            maxHeight: '90vh',
                            overflowY: 'auto',
                            border: '1px solid #333',
                            direction: isRTL ? 'rtl' : 'ltr'
                        }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexDirection: isRTL ? 'row-reverse' : 'row' }}>
                            <h2 style={{ margin: 0, fontSize: '20px', color: 'white', textAlign: isRTL ? 'right' : 'left' }}>
                                {t('quotations.add.modals.addTax.title', { defaultValue: 'إضافة ضريبة جديدة' })}
                            </h2>
                            <button
                                onClick={() => setShowAddTaxModal(false)}
                                style={{
                                    backgroundColor: 'transparent',
                                    border: 'none',
                                    color: 'white',
                                    fontSize: '24px',
                                    cursor: 'pointer',
                                    padding: '0',
                                    width: '30px',
                                    height: '30px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                }}
                            >
                                <FaTimes />
                            </button>
                        </div>
                        <form onSubmit={handleAddTax}>
                            <div style={{ display: 'grid', gap: '20px' }}>
                                <div>
                                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: 'white', fontSize: '14px', textAlign: isRTL ? 'right' : 'left' }}>
                                        {t('quotations.add.modals.addTax.nameAr', { defaultValue: 'الاسم بالعربية' })} <span style={{ color: '#dc3545' }}>*</span>
                                    </label>
                                    <input
                                        type="text"
                                        name="name_ar"
                                        value={taxForm.name_ar}
                                        onChange={(e) => setTaxForm(prev => ({ ...prev, name_ar: e.target.value }))}
                                        required
                                        style={{
                                            width: '100%',
                                            padding: '12px 15px',
                                            backgroundColor: '#1a1f2e',
                                            border: '1px solid #333',
                                            borderRadius: '8px',
                                            color: 'white',
                                            fontSize: '14px',
                                            direction: 'rtl',
                                            textAlign: 'right'
                                        }}
                                    />
                                </div>
                                <div>
                                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: 'white', fontSize: '14px', textAlign: isRTL ? 'right' : 'left' }}>
                                        {t('quotations.add.modals.addTax.nameEn', { defaultValue: 'الاسم بالإنجليزية' })} <span style={{ color: '#dc3545' }}>*</span>
                                    </label>
                                    <input
                                        type="text"
                                        name="name_en"
                                        value={taxForm.name_en}
                                        onChange={(e) => setTaxForm(prev => ({ ...prev, name_en: e.target.value }))}
                                        required
                                        style={{
                                            width: '100%',
                                            padding: '12px 15px',
                                            backgroundColor: '#1a1f2e',
                                            border: '1px solid #333',
                                            borderRadius: '8px',
                                            color: 'white',
                                            fontSize: '14px',
                                            direction: 'ltr',
                                            textAlign: 'left'
                                        }}
                                    />
                                </div>
                                <div>
                                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: 'white', fontSize: '14px', textAlign: isRTL ? 'right' : 'left' }}>
                                        {t('quotations.add.modals.addTax.rate', { defaultValue: 'النسبة (%)' })} <span style={{ color: '#dc3545' }}>*</span>
                                    </label>
                                    <input
                                        type="number"
                                        name="rate"
                                        value={taxForm.rate}
                                        onChange={(e) => setTaxForm(prev => ({ ...prev, rate: e.target.value }))}
                                        required
                                        style={{
                                            width: '100%',
                                            padding: '12px 15px',
                                            backgroundColor: '#1a1f2e',
                                            border: '1px solid #333',
                                            borderRadius: '8px',
                                            color: 'white',
                                            fontSize: '14px',
                                            textAlign: isRTL ? 'right' : 'left'
                                        }}
                                    />
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: 15 }}>
                                    <input
                                        type="checkbox"
                                        name="is_active"
                                        checked={taxForm.is_active}
                                        onChange={(e) => setTaxForm(prev => ({ ...prev, is_active: e.target.checked }))}
                                        style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                                    />
                                    <label style={{ color: 'white', fontSize: '14px', cursor: 'pointer' }}>
                                        {t('quotations.add.modals.addTax.isActive', { defaultValue: 'نشط' })}
                                    </label>
                                </div>
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'center', gap: '15px', marginTop: '30px' }}>
                                <button
                                    type="button"
                                    onClick={() => setShowAddTaxModal(false)}
                                    style={{ backgroundColor: '#6c757d', color: 'white', border: 'none', padding: '12px 25px', borderRadius: '8px', fontSize: '14px', fontWeight: 'bold', cursor: 'pointer' }}
                                >
                                    {t('common.cancel', { defaultValue: 'إلغاء' })}
                                </button>
                                <button
                                    type="submit"
                                    style={{ backgroundColor: '#AC2000', color: 'white', border: 'none', padding: '12px 25px', borderRadius: '8px', fontSize: '14px', fontWeight: 'bold', cursor: 'pointer' }}
                                >
                                    {t('common.save', { defaultValue: 'حفظ' })}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
                , document.body)}

            {showAddCostCenterModal && createPortal(
                <div
                    style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: 'rgba(0, 0, 0, 0.7)',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        zIndex: 999999,
                        padding: '20px'
                    }}
                    onClick={() => setShowAddCostCenterModal(false)}
                >
                    <div
                        style={{
                            backgroundColor: '#1b3350',
                            borderRadius: '12px',
                            padding: '30px',
                            maxWidth: '500px',
                            width: '100%',
                            maxHeight: '90vh',
                            overflowY: 'auto',
                            border: '1px solid #333',
                            direction: isRTL ? 'rtl' : 'ltr'
                        }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexDirection: isRTL ? 'row-reverse' : 'row' }}>
                            <h2 style={{ margin: 0, fontSize: '20px', color: 'white', textAlign: isRTL ? 'right' : 'left' }}>
                                {t('quotations.add.modals.addCostCenter.title', { defaultValue: 'إضافة مركز تكلفة جديد' })}
                            </h2>
                            <button
                                onClick={() => setShowAddCostCenterModal(false)}
                                style={{
                                    backgroundColor: 'transparent',
                                    border: 'none',
                                    color: 'white',
                                    fontSize: '24px',
                                    cursor: 'pointer',
                                    padding: '0',
                                    width: '30px',
                                    height: '30px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                }}
                            >
                                <FaTimes />
                            </button>
                        </div>
                        <form onSubmit={handleAddCostCenter}>
                            <div style={{ display: 'grid', gap: '20px' }}>
                                <div>
                                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: 'white', fontSize: '14px', textAlign: isRTL ? 'right' : 'left' }}>
                                        {t('quotations.add.modals.addCostCenter.nameAr', { defaultValue: 'الاسم بالعربية' })} <span style={{ color: '#dc3545' }}>*</span>
                                    </label>
                                    <input
                                        type="text"
                                        name="name_ar"
                                        value={costCenterForm.name_ar}
                                        onChange={(e) => setCostCenterForm(prev => ({ ...prev, name_ar: e.target.value }))}
                                        required
                                        style={{
                                            width: '100%',
                                            padding: '12px 15px',
                                            backgroundColor: '#1a1f2e',
                                            border: '1px solid #333',
                                            borderRadius: '8px',
                                            color: 'white',
                                            fontSize: '14px',
                                            direction: 'rtl',
                                            textAlign: 'right'
                                        }}
                                    />
                                </div>
                                <div>
                                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: 'white', fontSize: '14px', textAlign: isRTL ? 'right' : 'left' }}>
                                        {t('quotations.add.modals.addCostCenter.nameEn', { defaultValue: 'الاسم بالإنجليزية' })} <span style={{ color: '#dc3545' }}>*</span>
                                    </label>
                                    <input
                                        type="text"
                                        name="name_en"
                                        value={costCenterForm.name_en}
                                        onChange={(e) => setCostCenterForm(prev => ({ ...prev, name_en: e.target.value }))}
                                        required
                                        style={{
                                            width: '100%',
                                            padding: '12px 15px',
                                            backgroundColor: '#1a1f2e',
                                            border: '1px solid #333',
                                            borderRadius: '8px',
                                            color: 'white',
                                            fontSize: '14px',
                                            direction: 'ltr',
                                            textAlign: 'left'
                                        }}
                                    />
                                </div>
                                <div>
                                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: 'white', fontSize: '14px', textAlign: isRTL ? 'right' : 'left' }}>
                                        {t('quotations.add.modals.addCostCenter.code', { defaultValue: 'الكود' })}
                                    </label>
                                    <input
                                        type="text"
                                        name="code"
                                        value={costCenterForm.code}
                                        onChange={(e) => setCostCenterForm(prev => ({ ...prev, code: e.target.value }))}
                                        style={{
                                            width: '100%',
                                            padding: '12px 15px',
                                            backgroundColor: '#1a1f2e',
                                            border: '1px solid #333',
                                            borderRadius: '8px',
                                            color: 'white',
                                            fontSize: '14px',
                                            direction: isRTL ? 'rtl' : 'ltr',
                                            textAlign: isRTL ? 'right' : 'left'
                                        }}
                                    />
                                </div>
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'center', gap: '15px', marginTop: '30px' }}>
                                <button
                                    type="button"
                                    onClick={() => setShowAddCostCenterModal(false)}
                                    style={{ backgroundColor: '#6c757d', color: 'white', border: 'none', padding: '12px 25px', borderRadius: '8px', fontSize: '14px', fontWeight: 'bold', cursor: 'pointer' }}
                                >
                                    {t('common.cancel', { defaultValue: 'إلغاء' })}
                                </button>
                                <button
                                    type="submit"
                                    style={{ backgroundColor: '#AC2000', color: 'white', border: 'none', padding: '12px 25px', borderRadius: '8px', fontSize: '14px', fontWeight: 'bold', cursor: 'pointer' }}
                                >
                                    {t('common.save', { defaultValue: 'حفظ' })}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
                , document.body)}
        </div>
    );
};

const DropdownWithSearch = ({ isOpen, onToggle, searchTerm, onSearchChange, searchInputRef, items, pagination, currentPage, onPageChange, onSelect, label }) => {
    const { t, i18n } = useTranslation('global');
    const isRTL = i18n.language === 'ar';
    const internalRef = useRef(null);
    const inputRef = searchInputRef || internalRef;

    useEffect(() => {
        if (isOpen && inputRef?.current) {
            setTimeout(() => inputRef.current.focus(), 0);
        }
    }, [isOpen]);

    if (!isOpen) return null;

    return (
        <div style={{ position: 'absolute', zIndex: 10, top: '100%', left: 0, right: 0, background: '#1a1f2e', border: '1px solid #333', borderRadius: 8, marginTop: 4, maxHeight: 400, overflowY: 'auto' }} onClick={e => e.stopPropagation()}>
            <div style={{ padding: 8 }}>
                <input ref={inputRef} type="text" placeholder={`${t('common.search')} ${label}`} value={searchTerm} onChange={e => onSearchChange(e.target.value)} style={{ width: '100%', padding: 8, backgroundColor: '#14263b', border: '1px solid #444', borderRadius: 6, color: 'white', fontSize: 14 }} />
            </div>
            {pagination && pagination.last_page > 1 && (
                <div style={{ padding: '8px', borderTop: '1px solid #333', background: '#14263b' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                        <span style={{ color: '#ccc', fontSize: 11 }}>{t('notesPayable.form.page')} {pagination.current_page} {t('notesPayable.form.of')} {pagination.last_page}</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 4, flexWrap: 'wrap' }}>
                        <button
                            type="button"
                            onClick={() => onPageChange(1)}
                            disabled={pagination.current_page === 1}
                            style={{
                                padding: '4px 8px',
                                backgroundColor: pagination.current_page === 1 ? '#2a2a2a' : '#1a1f2e',
                                color: pagination.current_page === 1 ? '#666' : 'white',
                                border: '1px solid #444',
                                borderRadius: '4px',
                                cursor: pagination.current_page === 1 ? 'not-allowed' : 'pointer',
                                fontSize: 10,
                                fontWeight: 'bold',
                                minWidth: '28px',
                            }}
                            title={t('notesPayable.form.firstPage', { defaultValue: 'First' })}
                        >
                            ««
                        </button>
                        <button
                            type="button"
                            onClick={() => onPageChange(pagination.current_page - 1)}
                            disabled={pagination.current_page === 1}
                            style={{
                                padding: '4px 8px',
                                backgroundColor: pagination.current_page === 1 ? '#2a2a2a' : '#1a1f2e',
                                color: pagination.current_page === 1 ? '#666' : 'white',
                                border: '1px solid #444',
                                borderRadius: '4px',
                                cursor: pagination.current_page === 1 ? 'not-allowed' : 'pointer',
                                fontSize: 10,
                                fontWeight: 'bold',
                                minWidth: '28px',
                            }}
                            title={t('notesPayable.form.previous', { defaultValue: 'Previous' })}
                        >
                            ‹
                        </button>
                        {(() => {
                            const pages = [];
                            const current = pagination.current_page;
                            const last = pagination.last_page;
                            const showPages = 5;
                            let start = Math.max(1, current - Math.floor(showPages / 2));
                            let end = Math.min(last, start + showPages - 1);
                            if (end - start < showPages - 1) {
                                start = Math.max(1, end - showPages + 1);
                            }
                            for (let i = start; i <= end; i++) {
                                pages.push(i);
                            }
                            return pages.map((page) => (
                                <button
                                    key={page}
                                    type="button"
                                    onClick={() => onPageChange(page)}
                                    style={{
                                        padding: '4px 8px',
                                        backgroundColor: page === current ? '#AC2000' : '#1a1f2e',
                                        color: 'white',
                                        border: page === current ? '1px solid #AC2000' : '1px solid #444',
                                        borderRadius: '4px',
                                        cursor: 'pointer',
                                        fontSize: 10,
                                        fontWeight: page === current ? 'bold' : 'normal',
                                        minWidth: '28px',
                                    }}
                                >
                                    {page}
                                </button>
                            ));
                        })()}
                        <button
                            type="button"
                            onClick={() => onPageChange(pagination.current_page + 1)}
                            disabled={pagination.current_page === pagination.last_page}
                            style={{
                                padding: '4px 8px',
                                backgroundColor: pagination.current_page === pagination.last_page ? '#2a2a2a' : '#1a1f2e',
                                color: pagination.current_page === pagination.last_page ? '#666' : 'white',
                                border: '1px solid #444',
                                borderRadius: '4px',
                                cursor: pagination.current_page === pagination.last_page ? 'not-allowed' : 'pointer',
                                fontSize: 10,
                                fontWeight: 'bold',
                                minWidth: '28px',
                            }}
                            title={t('notesPayable.form.next', { defaultValue: 'Next' })}
                        >
                            ›
                        </button>
                        <button
                            type="button"
                            onClick={() => onPageChange(pagination.last_page)}
                            disabled={pagination.current_page === pagination.last_page}
                            style={{
                                padding: '4px 8px',
                                backgroundColor: pagination.current_page === pagination.last_page ? '#2a2a2a' : '#1a1f2e',
                                color: pagination.current_page === pagination.last_page ? '#666' : 'white',
                                border: '1px solid #444',
                                borderRadius: '4px',
                                cursor: pagination.current_page === pagination.last_page ? 'not-allowed' : 'pointer',
                                fontSize: 10,
                                fontWeight: 'bold',
                                minWidth: '28px',
                            }}
                            title={t('notesPayable.form.lastPage', { defaultValue: 'Last' })}
                        >
                            »»
                        </button>
                    </div>
                </div>
            )}
            <div>
                {(Array.isArray(items) ? items : []).map(item => (
                    <div
                        key={item.id}
                        onClick={() => onSelect(item.id)}
                        style={{
                            padding: '12px 15px',
                            cursor: 'pointer',
                            borderTop: '1px solid #333',
                            fontSize: '14px',
                            color: 'white',
                            transition: 'background-color 0.2s',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center'
                        }}
                        onMouseEnter={e => e.currentTarget.style.backgroundColor = '#14263b'}
                        onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}
                    >
                        <span>
                            {`${item.code ? `${item.code} - ` : ''}${isRTL ? (item.name_ar || item.name || '') : (item.name_en || item.name_ar || item.name || '')}`}
                        </span>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default EditNotePayable;
